/**
 * Duration type mappings for converting to/from seconds
 */
export type DurationType = 'hrs' | 'hrs/min' | 'min' | 'min/sec' | 'sec';

export interface DurationDisplay {
  primary: number;
  secondary: number;
}

/** Normalized display strings for form inputs (primary as-is, secondary zero-padded when used) */
export interface DurationDisplayStrings {
  primary: string;
  secondary: string;
}

/**
 * Converts total seconds to display strings for form inputs.
 * Use this when binding to text inputs that show primary/secondary (e.g. "2" and "05").
 */
export function secondsToDurationDisplayStrings(
  totalSeconds: number,
  durationType: DurationType,
  hasSecondary: boolean
): DurationDisplayStrings {
  const d = secondsToDurationDisplay(totalSeconds, durationType);
  const primary = d.primary > 0 || d.secondary > 0 ? String(d.primary) : '';
  const secondary = hasSecondary ? String(d.secondary).padStart(2, '0') : '';
  return {primary, secondary};
}

/**
 * Converts total seconds to display values based on duration type
 * @param totalSeconds - Total seconds to convert
 * @param durationType - Type of duration format
 * @returns Object with primary and secondary display values
 */
export function secondsToDurationDisplay(totalSeconds: number, durationType: DurationType): DurationDisplay {
  if (isNaN(totalSeconds) || totalSeconds < 0) {
    return {primary: 0, secondary: 0};
  }

  switch (durationType) {
    case 'hrs/min': {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      return {primary: hours, secondary: minutes};
    }
    case 'min/sec': {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return {primary: minutes, secondary: seconds};
    }
    case 'hrs': {
      const hours = Math.floor(totalSeconds / 3600);
      return {primary: hours, secondary: 0};
    }
    case 'min': {
      const minutes = Math.floor(totalSeconds / 60);
      return {primary: minutes, secondary: 0};
    }
    case 'sec': {
      return {primary: totalSeconds, secondary: 0};
    }
    default:
      return {primary: 0, secondary: 0};
  }
}

/**
 * Converts duration display values to total seconds
 * @param primary - Primary value (hours or minutes depending on type)
 * @param secondary - Secondary value (minutes or seconds depending on type)
 * @param durationType - Type of duration format
 * @returns Total seconds
 */
export function durationDisplayToSeconds(primary: number, secondary: number, durationType: DurationType): number {
  const p = isNaN(primary) ? 0 : Math.max(0, primary);
  const s = isNaN(secondary) ? 0 : Math.max(0, secondary);

  switch (durationType) {
    case 'hrs/min':
      return p * 3600 + s * 60;
    case 'min/sec':
      return p * 60 + s;
    case 'hrs':
      return p * 3600;
    case 'min':
      return p * 60;
    case 'sec':
      return p;
    default:
      return 0;
  }
}

/**
 * Formats total seconds into a human-readable duration string
 * @param totalSeconds - Total seconds to format
 * @returns Formatted duration string (e.g., "2h 30m 15s")
 */
export function formatDuration(totalSeconds: number, longSuffix?: boolean): string {
  if (totalSeconds === 0) {
    return '0';
  }

  if (isNaN(totalSeconds) || totalSeconds < 0) {
    return '';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours}${longSuffix ? ' hrs' : 'h'}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}${longSuffix ? ' min' : 'm'}`);
  }
  if (seconds > 0) {
    parts.push(`${seconds}${longSuffix ? ' sec' : 's'}`);
  }

  return parts.join(' ');
}
